import React, { useEffect, useState } from 'react';
import axios from '../../axiosConfig';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext'; // 导入 AuthContext
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faMoneyBillWave,
  faMedal,
  faTicket,
  faEdit,
  faCreditCard,
  faUserPlus,
  faGift,
  faHeart,
  faComment,
  faClipboardList,
  faCalendarAlt,
  faSignOutAlt,
  faCoins,
  faArrowRight,
  faCheckCircle,
  faTimesCircle,
  faClock,
  faBlog
} from '@fortawesome/free-solid-svg-icons';
import '../../styles/UserCenter.css';
import imgPrefix from '../../imgConfig'; // 导入 imgConfig.js


const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth(); // 获取 AuthContext 的 logout 方法
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [message, setMessage] = useState('');
  const [yuyueRecords, setYuyueRecords] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState('');
  
  // 模拟优惠券数据（实际应从API获取）
  const [coupons, setCoupons] = useState(0);

  // 验证当前用户身份
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user || user.id !== parseInt(id, 10)) {
      setError(t('userMgmt.noPermission'));
      return;
    }
  }, [id]);

  // 获取用户详情
  useEffect(() => {
    axios
      .get(`/yonghu/detail/${id}`)
      .then((response) => {
        if (response.data.code === 0) {
          setUser(response.data.data);
          setFormData(response.data.data);
        } else {
          setError(response.data.msg || t('userMgmt.getUserInfoFailed'));
        }
      })
      .catch((err) => console.error('Error fetching user detail:', err));
  }, [id]);

  // 获取用户预约记录
  useEffect(() => {
    if (user?.zhanghao) {
      axios
        .get(`/jiaolianyuyue/${user.zhanghao}/yuyue`)
        .then((response) => {
          if (response.data.code === 0) {
            setYuyueRecords(response.data.data);

            // 更新用户对象和本地存储
            const updatedUser = { ...user, yuyueRecords: response.data.data };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
          }
        })
        .catch((err) => console.error('Error fetching yuyue records:', err));
    }
  }, [user?.zhanghao]);

  // 处理表单输入
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 上传头像文件
  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleUpload = () => {
    if (!selectedFile) {
      alert('请选择文件后再上传！');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);

    axios
      .post('/file/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((response) => {
        if (response.data.code === 0) {
          const uploadedFileName = response.data.file;
          setFormData((prev) => ({
            ...prev,
            tupian: `/upload/${uploadedFileName}`,
          }));
          setUser((prev) => ({
            ...prev,
            tupian: `/upload/${uploadedFileName}`,
          }));
          alert('头像上传成功！');
        } else {
          alert('上传失败，请重试！');
        }
      })
      .catch((error) => {
        console.error('Error uploading file:', error);
        alert('上传失败，请稍后重试！');
      });
  };

  // 提交修改
  const handleSubmit = (e) => {
    e.preventDefault();

    axios
      .put('/yonghu/update', formData)
      .then((response) => {
        if (response.data.code === 0) {
          setUser(formData);
          setIsEditing(false);
          setMessage('更新成功！');
        } else {
          setMessage(response.data.msg || '更新失败');
        }
      })
      .catch((err) => {
        console.error('Error updating user:', err);
        setMessage('请求失败，请稍后重试');
      });
  };

  // 获取预约状态类名
  const getStatusClass = (record) => {
    if (record.paymentStatus === 2) {
      return 'status-canceled';
    } else if (record.paymentStatus === 0) {
      return 'status-pending';
    } else if (record.paymentStatus === 1) {
      return 'status-confirmed';
    }
    return '';
  };

  // 获取预约状态文本
  const getStatusText = (record) => {
    if (record.paymentStatus === 2) {
      return '已取消';
    } else if (record.paymentStatus === 0) {
      return record.paymentMethod === 2 ? '待付款' : '待确认';
    } else if (record.paymentStatus === 1) {
      return '已确认';
    }
    return '未知状态';
  };

  // 跳转到充值页面
  const handleRecharge = () => {
    navigate('/recharge');
  };

  // 跳转到会员订阅页面
  const handleVip = () => {
    navigate('/vip-subscription');
  };

  // 跳转到礼品卡页面
  const handleGiftCard = () => {
    navigate('/gift-cards');
  };

  // 跳转到收藏页面
  const handleFavorites = () => {
    navigate('/favorites');
  };

  // 跳转到评论页面
  const handleComments = () => {
    navigate('/my-comments');
  };

  // 处理其他功能按钮点击
  const handleFeatureClick = (feature) => {
    // 根据点击的功能执行不同的操作
    switch (feature) {
      case 'appointments':
        navigate('/user-appointment');
        break;
      case 'recharge':
        navigate('/recharge');
        break;
      case 'vip':
        navigate('/vip-subscription');
        break;
      case 'giftcard':
        navigate('/gift-cards');
        break;
      case 'favorites':
        navigate('/favorites');
        break;
      case 'comments':
        navigate('/my-comments');
        break;
      case 'orders':
        navigate('/user-appointment');
        break;
      case 'logout':
        logout(); // 使用 AuthContext 的 logout 方法
        navigate('/'); // 导航到首页
        break;
      default:
        break;
    }
  };

  // 取消未支付的预约
  const handleCancelAppointment = (appointmentId) => {
    if (window.confirm('确定要取消这个预约吗？')) {
      axios.post(`/jiaolianyuyue/cancelUnpaidAppointment?appointmentId=${appointmentId}`)
        .then(response => {
          if (response.data.code === 0) {
            // 更新预约记录列表
            setYuyueRecords(yuyueRecords.filter(record => record.id !== appointmentId));
            alert('预约已取消');
          } else {
            alert(response.data.msg || '取消预约失败');
          }
        })
        .catch(error => {
          console.error('Error canceling appointment:', error);
          alert('取消预约失败，请稍后重试');
        });
    }
  };

  if (error) {
    return <div style={{ color: 'red' }}>{error}</div>;
  }

  if (!user) {
    return <div>{t('userMgmt.loading')}</div>;
  }

  return (
    <div className="user-center-container">
      {!isEditing ? (
        <>
          {/* 用户信息头部 */}
          <div className="user-center-header">
            <img 
              src={user.tupian || `${imgPrefix}/upload/default-avatar.png`} 
              alt={user.xingming} 
              className="user-avatar" 
            />
            <div className="user-info">
              <h1>{user.xingming}</h1>
              <p>{t('userMgmt.account')}: {user.zhanghao}</p>
              <div className="user-tags">
                <span className="user-tag">{user.xingbie}</span>
                {user.vipLevel && (
                  <span className="user-tag vip-tag">VIP {user.vipLevel}</span>
                )}
              </div>
            </div>
            <div className="info-btns">
            <button className="btn btn-primary" onClick={() => setIsEditing(true)}>
              <FontAwesomeIcon icon={faEdit} /> {t('userMgmt.editProfile')}
            </button>

            <button className="btn btn-primary" onClick={() => handleFeatureClick('logout')}>
              <FontAwesomeIcon icon={faSignOutAlt} /> {t('userMgmt.logout')}
            </button>
</div>
          </div>

          {/* 账户统计卡片 */}
          <div className="account-stats">
            <div className="stat-card stat-card-balance">
              <div className="stat-title">
                <FontAwesomeIcon icon={faMoneyBillWave} />
                <span>{t('userCenter.balance')}</span>
              </div>
              <div className="stat-value">¥ {user.yue || '0.00'}</div>
              <div className="stat-actions">
                <a href="#" className="stat-link" onClick={handleRecharge}>
                  {t('recharge.title')} <FontAwesomeIcon icon={faArrowRight} />
                </a>
              </div>
            </div>

            <div className="stat-card stat-card-points">
              <div className="stat-title">
                <FontAwesomeIcon icon={faCoins} />
                <span>{t('userCenter.points')}</span>
              </div>
              <div className="stat-value">{user.jifen || '0'}</div>
              <div className="stat-actions">
                <a href="#" className="stat-link">
                  {t('userCenter.points')} <FontAwesomeIcon icon={faArrowRight} />
                </a>
              </div>
            </div>

            <div className="stat-card stat-card-coupons">
              <div className="stat-title">
                <FontAwesomeIcon icon={faTicket} />
                <span>{t('userCenter.coupons')}</span>
              </div>
              <div className="stat-value">{coupons}</div>
              <div className="stat-actions">
                <a href="#" className="stat-link">
                  {t('userCenter.coupons')} <FontAwesomeIcon icon={faArrowRight} />
                </a>
              </div>
            </div>
          </div>

          {/* 功能区域 */}
          <div className="feature-section">
            <h2 className="section-title">
              <FontAwesomeIcon icon={faUser} />
              <span>{t('userCenter.commonFunctions')}</span>
            </h2>
            <div className="feature-grid">
              <div className="feature-item" onClick={() => handleFeatureClick('recharge')}>
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faCreditCard} />
                </div>
                <div className="feature-name">{t('recharge.title')}</div>
              </div>
              <div className="feature-item" onClick={() => handleFeatureClick('vip')}>
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faUserPlus} />
                </div>
                <div className="feature-name">{user.vipLevel ? t('vip.memberLevel') : t('vip.title')}</div>
                {user.vipLevel && <div className="feature-badge">VIP</div>}
              </div>
              <div className="feature-item" onClick={() => handleFeatureClick('giftcard')}>
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faGift} />
                </div>
                <div className="feature-name">{t('giftCards.title')}</div>
              </div>
              <div className="feature-item" onClick={() => handleFeatureClick('favorites')}>
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faHeart} />
                </div>
                <div className="feature-name">{t('userMgmt.favorites')}</div>
              </div>
              <div className="feature-item" onClick={() => handleFeatureClick('comments')}>
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faComment} />
                </div>
                <div className="feature-name">{t('userMgmt.comments')}</div>
              </div>
              <div className="feature-item" onClick={() => handleFeatureClick('orders')}>
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faClipboardList} />
                </div>
                <div className="feature-name">{t('booking.title')}</div>
              </div>
              <div className="feature-item" onClick={() => handleFeatureClick('appointments')}>
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faCalendarAlt} />
                </div>
                <div className="feature-name">{t('booking.appointmentDetail')}</div>
              </div>
              <div className="feature-item" onClick={() => handleFeatureClick('logout')}>
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faSignOutAlt} />
                </div>
                <div className="feature-name">{t('userMgmt.logout')}</div>
              </div>
            </div>
          </div>

          {/* 预约记录 */}
          <div className="appointment-section">
            <h2 className="section-title">
              <FontAwesomeIcon icon={faCalendarAlt} />
              <span>{t('userCenter.recentAppointments')}</span>
            </h2>
            
            {yuyueRecords.length === 0 ? (
              <p>{t('userCenter.noAppointments')}</p>
            ) : (
              <div className="appointment-list">
                {yuyueRecords.slice(0, 3).map((record) => (
                  <div key={record.id} className="appointment-item">
                    <div className="appointment-info">
                      <div className="appointment-id">{t('userCenter.appointmentNumber')}: {record.id}</div>
                      <div className="appointment-coach">{t('userCenter.coach')}: {record.jiaolianbianhao}</div>
                      <div className="appointment-time">
                        {t('userCenter.time')}: {new Date(record.startTime).toLocaleString()}
                      </div>
                      <div className="appointment-payment">
                        {t('userCenter.paymentMethod')}: {record.paymentMethod === 2 ? t('userCenter.offlinePayment') : t('userCenter.onlinePayment')}
                      </div>
                    </div>
                    <div className="appointment-actions">
                      <div className={`appointment-status ${getStatusClass(record)}`}>
                        {getStatusText(record)}
                      </div>
                      {record.paymentStatus === 0 && (
                        <button 
                          className="btn-cancel"
                          onClick={() => handleCancelAppointment(record.id)}
                        >
                          {t('userCenter.cancelAppointment')}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {yuyueRecords.length > 3 && (
                  <div style={{ textAlign: 'center', padding: '10px' }}>
                    <Link to="/user-appointment" className="stat-link">
                      {t('userCenter.viewMore')} <FontAwesomeIcon icon={faArrowRight} />
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="edit-form">
          <h2 className="section-title">
            <FontAwesomeIcon icon={faEdit} />
            <span>编辑个人资料</span>
          </h2>
        <form onSubmit={handleSubmit}>
            <div className="form-group">
            <label>账号:</label>
            <input
              type="text"
              name="zhanghao"
              value={formData.zhanghao}
              onChange={handleInputChange}
              required
            />
          </div>
            <div className="form-group">
            <label>姓名:</label>
            <input
              type="text"
              name="xingming"
              value={formData.xingming}
              onChange={handleInputChange}
            />
          </div>
            <div className="form-group">
            <label>性别:</label>
            <input
              type="text"
              name="xingbie"
              value={formData.xingbie}
              onChange={handleInputChange}
            />
          </div>
            <div className="form-group">
            <label>手机:</label>
            <input
              type="text"
              name="shouji"
              value={formData.shouji}
              onChange={handleInputChange}
            />
          </div>
            <div className="form-group">
            <label>地址:</label>
            <input
              type="text"
              name="dizhi"
              value={formData.dizhi}
              onChange={handleInputChange}
            />
          </div>
            <div className="form-group">
            <label>图片链接:</label>
            <input
              type="text"
              name="tupian"
              value={formData.tupian}
              onChange={handleInputChange}
            />
          </div>
            <div className="form-group">
            <label>上传图片:</label>
            <input type="file" onChange={handleFileChange} />
              <button type="button" className="btn btn-secondary" onClick={handleUpload}>
              上传图片
            </button>
          </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">保存</button>
              <button type="button" className="btn btn-secondary" onClick={() => setIsEditing(false)}>
            取消
          </button>
            </div>
        </form>
        </div>
      )}

      {message && <p style={{ color: 'green' }}>{message}</p>}
    </div>
  );
};

export default UserDetail;
